<template>
  <div
    ref="viewport"
    class="compare-slider"
    :class="{ 'compare-slider--pannable': canPan, 'compare-slider--panning': mode === 'pan' }"
    @pointerdown="onViewportPointerDown"
    @wheel.prevent="onWheel"
    @dblclick="onDblClick"
  >
    <!-- Enhanced (base) layer -->
    <img
      :src="afterSrc"
      class="compare-layer"
      :style="layerStyle"
      alt="Enhanced"
      draggable="false"
      @load="onAfterLoad"
    />
    <!-- Original, masked to the left of the divider -->
    <div class="compare-before-mask" :style="{ width: divider + '%' }">
      <img
        :src="beforeSrc"
        class="compare-layer"
        :style="layerStyle"
        alt="Original"
        draggable="false"
      />
    </div>

    <span v-show="divider > 14" class="compare-label compare-label--left">
      Original
    </span>
    <span v-show="divider < 86" class="compare-label compare-label--right">
      Enhanced
    </span>

    <!-- Divider handle -->
    <div class="compare-handle" :style="{ left: divider + '%' }">
      <div class="compare-handle-line"></div>
      <div
        class="compare-handle-grip"
        @pointerdown.stop.prevent="onDividerPointerDown"
      >
        <v-icon small color="white">mdi-code-tags</v-icon>
      </div>
    </div>

    <!-- Zoom controls -->
    <div class="compare-zoom" @pointerdown.stop @dblclick.stop>
      <v-btn x-small icon dark class="compare-zoom-btn" @click="zoomStep(1 / 1.4)">
        <v-icon small>mdi-minus</v-icon>
      </v-btn>
      <span class="compare-zoom-label">{{ zoomLabel }}</span>
      <v-btn x-small icon dark class="compare-zoom-btn" @click="zoomStep(1.4)">
        <v-icon small>mdi-plus</v-icon>
      </v-btn>
      <v-btn x-small text dark class="compare-zoom-btn" @click="goFit">Fit</v-btn>
      <v-btn x-small text dark class="compare-zoom-btn" @click="goActual">1:1</v-btn>
    </div>
  </div>
</template>

<script>
// Before/after comparison with synchronized zoom + pan.
//
// Both layers render the image at natW*scale x natH*scale (the enhanced
// image's natural size is the reference; the original shares its aspect
// ratio) translated by (ox, oy) inside a fixed-height viewport. The
// "before" layer sits in a mask clipped at the divider. Dragging the grip
// moves the divider; dragging anywhere else pans; wheel/pinch zooms.
// The initial view fits the whole image; double-click or the 1:1 button
// jumps to the zoom level where the enhancement detail is visible.
const MAX_SCALE = 6;

export default {
  name: "CompareSlider",
  props: {
    beforeSrc: { type: String, required: true },
    afterSrc: { type: String, required: true }
  },
  data() {
    return {
      natW: 0,
      natH: 0,
      vw: 0,
      vh: 0,
      scale: 1,
      ox: 0,
      oy: 0,
      divider: 50,
      mode: null, // "divider" | "pan" | "pinch"
      lastX: 0,
      lastY: 0,
      pointers: {},
      pinchStartDist: 0,
      pinchStartScale: 1
    };
  },
  computed: {
    layerStyle() {
      return {
        width: this.natW * this.scale + "px",
        height: this.natH * this.scale + "px",
        transform: "translate(" + this.ox + "px, " + this.oy + "px)"
      };
    },
    canPan() {
      return (
        this.natW * this.scale > this.vw + 1 ||
        this.natH * this.scale > this.vh + 1
      );
    },
    zoomLabel() {
      return Math.round(this.scale * 100) + "%";
    }
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    this.unbindWindow();
  },
  methods: {
    // ------------------------------------------------------------- setup
    onAfterLoad(event) {
      this.natW = event.target.naturalWidth || 1;
      this.natH = event.target.naturalHeight || 1;
      this.measure();
      // Default view: when the image dwarfs the viewport, open at 1:1 on
      // the upper-middle area (faces live there in nearly every portrait) -
      // fit-to-screen hides exactly the rebuilt skin/hair detail the
      // product sells. Small images still open fitted.
      if (this.fitScale() < 0.6) {
        this.goDetail();
      } else {
        this.goFit();
      }
    },
    goDetail() {
      this.scale = this.clampScale(1);
      this.ox = this.vw / 2 - this.natW * this.scale * 0.5;
      this.oy = this.vh * 0.45 - this.natH * this.scale * 0.3;
      this.clampOffsets();
    },
    measure() {
      const viewport = this.$refs.viewport;
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      this.vw = rect.width;
      this.vh = rect.height;
    },
    onResize() {
      this.measure();
      this.scale = this.clampScale(this.scale);
      this.clampOffsets();
    },
    fitScale() {
      if (!this.natW || !this.natH || !this.vw || !this.vh) return 1;
      return Math.min(this.vw / this.natW, this.vh / this.natH);
    },
    clampScale(s) {
      // Never force stretching beyond 1:1 as the minimum for small images.
      const min = Math.min(this.fitScale(), 1);
      if (s < min) return min;
      if (s > MAX_SCALE) return MAX_SCALE;
      return s;
    },
    centerImage() {
      this.ox = (this.vw - this.natW * this.scale) / 2;
      this.oy = (this.vh - this.natH * this.scale) / 2;
      this.clampOffsets();
    },
    clampOffsets() {
      const imgW = this.natW * this.scale;
      const imgH = this.natH * this.scale;
      if (imgW <= this.vw) {
        this.ox = (this.vw - imgW) / 2;
      } else {
        this.ox = Math.min(0, Math.max(this.vw - imgW, this.ox));
      }
      if (imgH <= this.vh) {
        this.oy = (this.vh - imgH) / 2;
      } else {
        this.oy = Math.min(0, Math.max(this.vh - imgH, this.oy));
      }
    },

    // -------------------------------------------------------------- zoom
    zoomAt(cx, cy, factor) {
      const next = this.clampScale(this.scale * factor);
      const ratio = next / this.scale;
      this.ox = cx - (cx - this.ox) * ratio;
      this.oy = cy - (cy - this.oy) * ratio;
      this.scale = next;
      this.clampOffsets();
    },
    zoomStep(factor) {
      this.zoomAt(this.vw / 2, this.vh / 2, factor);
    },
    goFit() {
      this.scale = this.clampScale(this.fitScale());
      this.centerImage();
    },
    goActual() {
      this.scale = this.clampScale(1);
      this.centerImage();
    },
    onWheel(event) {
      this.measure();
      const rect = this.$refs.viewport.getBoundingClientRect();
      const factor = event.deltaY < 0 ? 1.15 : 1 / 1.15;
      this.zoomAt(event.clientX - rect.left, event.clientY - rect.top, factor);
    },
    onDblClick(event) {
      const rect = this.$refs.viewport.getBoundingClientRect();
      const fitVal = this.clampScale(this.fitScale());
      if (Math.abs(this.scale - fitVal) < 0.01) {
        // At fit → jump to 1:1 anchored on the clicked spot.
        this.zoomAt(
          event.clientX - rect.left,
          event.clientY - rect.top,
          this.clampScale(1) / this.scale
        );
      } else {
        this.goFit();
      }
    },

    // ---------------------------------------------------- pointer input
    onDividerPointerDown(event) {
      this.mode = "divider";
      this.trackPointer(event);
      this.bindWindow();
      this.updateDivider(event.clientX);
    },
    onViewportPointerDown(event) {
      event.preventDefault();
      this.trackPointer(event);
      const count = Object.keys(this.pointers).length;
      if (count === 2) {
        this.mode = "pinch";
        this.pinchStartDist = this.pinchDistance();
        this.pinchStartScale = this.scale;
      } else if (this.mode !== "divider") {
        this.mode = "pan";
        this.lastX = event.clientX;
        this.lastY = event.clientY;
      }
      this.bindWindow();
    },
    onPointerMove(event) {
      if (this.pointers[event.pointerId]) {
        this.pointers[event.pointerId] = { x: event.clientX, y: event.clientY };
      }
      if (this.mode === "divider") {
        this.updateDivider(event.clientX);
      } else if (this.mode === "pinch") {
        const dist = this.pinchDistance();
        if (this.pinchStartDist > 0 && dist > 0) {
          const mid = this.pinchMidpoint();
          const rect = this.$refs.viewport.getBoundingClientRect();
          const target = this.clampScale(
            this.pinchStartScale * (dist / this.pinchStartDist)
          );
          this.zoomAt(mid.x - rect.left, mid.y - rect.top, target / this.scale);
        }
      } else if (this.mode === "pan") {
        this.ox += event.clientX - this.lastX;
        this.oy += event.clientY - this.lastY;
        this.lastX = event.clientX;
        this.lastY = event.clientY;
        this.clampOffsets();
      }
    },
    onPointerUp(event) {
      delete this.pointers[event.pointerId];
      const count = Object.keys(this.pointers).length;
      if (count === 0) {
        this.mode = null;
        this.unbindWindow();
      } else if (this.mode === "pinch" && count === 1) {
        // Fall back to panning with the remaining finger.
        this.mode = "pan";
        const remaining = this.firstPointer();
        if (remaining) {
          this.lastX = remaining.x;
          this.lastY = remaining.y;
        }
      }
    },
    trackPointer(event) {
      this.pointers[event.pointerId] = { x: event.clientX, y: event.clientY };
    },
    firstPointer() {
      const ids = Object.keys(this.pointers);
      return ids.length ? this.pointers[ids[0]] : null;
    },
    pinchDistance() {
      const ids = Object.keys(this.pointers);
      if (ids.length < 2) return 0;
      const a = this.pointers[ids[0]];
      const b = this.pointers[ids[1]];
      return Math.hypot(a.x - b.x, a.y - b.y);
    },
    pinchMidpoint() {
      const ids = Object.keys(this.pointers);
      const a = this.pointers[ids[0]];
      const b = this.pointers[ids[1]];
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    },
    updateDivider(clientX) {
      const viewport = this.$refs.viewport;
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      if (!rect.width) return;
      let pct = ((clientX - rect.left) / rect.width) * 100;
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      this.divider = pct;
    },
    bindWindow() {
      window.addEventListener("pointermove", this.onPointerMove);
      window.addEventListener("pointerup", this.onPointerUp);
      window.addEventListener("pointercancel", this.onPointerUp);
    },
    unbindWindow() {
      window.removeEventListener("pointermove", this.onPointerMove);
      window.removeEventListener("pointerup", this.onPointerUp);
      window.removeEventListener("pointercancel", this.onPointerUp);
    }
  }
};
</script>

<style scoped>
.compare-slider {
  position: relative;
  overflow: hidden;
  height: 72vh;
  border-radius: 10px;
  touch-action: none;
  user-select: none;
  background: var(--bg-secondary, #1b1b25);
}

.compare-slider--pannable {
  cursor: grab;
}

.compare-slider--panning {
  cursor: grabbing;
}

.compare-layer {
  position: absolute;
  top: 0;
  left: 0;
  max-width: none;
  transform-origin: 0 0;
  will-change: transform;
}

.compare-before-mask {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
}

.compare-label {
  position: absolute;
  top: 10px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  pointer-events: none;
  z-index: 2;
}

.compare-label--left {
  left: 10px;
}

.compare-label--right {
  right: 10px;
}

.compare-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  z-index: 2;
}

.compare-handle-line {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -1px;
  width: 2px;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

.compare-handle-grip {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50%, -50%);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--accent, #a855f7);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
  cursor: ew-resize;
  touch-action: none;
}

.compare-zoom {
  position: absolute;
  right: 10px;
  bottom: 10px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.6);
  z-index: 3;
}

.compare-zoom-label {
  min-width: 44px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
}

.compare-zoom-btn {
  color: #fff !important;
}
</style>
